import { Injectable, signal } from '@angular/core';
import { BaseHttpService } from './base-http.service';
import {
  MarkPointsToBeCorrected,
  Meeting,
  MeetingAction,
  MeetingDetail,
  MeetingReorder,
  MeetingsQuery,
  NewMeeting,
  NewMeetingEvent,
  NewMeetingParticipant,
  ReportMeetingPoints,
  UpdateMeeting,
  UpdateMeetingEvent,
  UpdateMeetingParticipant,
  UpdateMeetingPointVotes,
} from '../interfaces/meetings';
import { finalize, map, Observable, tap } from 'rxjs';
import { buildQueryParams } from '../helpers/build-query-params';
import {
  MeetingDetailDto,
  MeetingDto,
  MeetingHistoryItemDto,
  MeetingParticipantDto,
  MeetingRecipientDto,
} from '../interfaces/dtos/meetings';
import { MeetingsMapper, ObjectUtils } from '../utils';
import { IncludedPointDto, PointDto, PointVotesDetailDto, PointWithEventsDto } from '../interfaces/dtos/points';
import { PointTreeLevelDto } from '../interfaces/dtos/points/point-tree-dto.interface';
import { MeetingTypeDto } from '@shared/interfaces/dtos/global';
import { PaginatedItems } from '@shared/types/dev';
import { AddRecipientsToMeeting } from '@shared/interfaces/points';
import { MeetingRecipient } from '@shared/interfaces/meetings/meeting-recipients.interface';
import { PointsMapper } from '@shared/utils/points-mapper.util';

@Injectable({
  providedIn: 'root',
})
export class MeetingsService extends BaseHttpService {
  protected endPoint: string = 'Seances';

  public readonly totalCount = signal<number>(-1);
  public readonly totalPages = signal<number>(-1);
  public readonly pageNumber = signal<number>(1);
  public readonly pageSize = signal<number>(50);

  private setState({
    totalCount,
    totalPages,
    pageNumber,
    pageSize,
  }: {
    totalCount: number;
    totalPages: number;
    pageNumber: number;
    pageSize: number;
  }): void {
    this.totalCount.set(totalCount);
    this.totalPages.set(totalPages);
    this.pageNumber.set(pageNumber);
    this.pageSize.set(pageSize);
  }

  public insertNewMeeting(dto: NewMeeting): Observable<unknown> {
    return this.http
      .post<MeetingDetailDto>(`${this.buildURL()}`, dto)
      .pipe(map((meeting) => MeetingsMapper.toMeetingDetail(meeting)));
  }

  public insertNewMeetingParticipant(meetingId: number, dto: NewMeetingParticipant): Observable<unknown> {
    return this.http.post(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Participants`, dto);
  }

  public insertNewMeetingEvent(meetingId: number, dto: NewMeetingEvent): Observable<unknown> {
    return this.http.post(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Events`, dto);
  }

  public fetchOneMeeting(meetingId: number): Observable<MeetingDetail> {
    return this.http
      .get<MeetingDetailDto>(`${this.buildURL({ idOrSubEndpoint: meetingId })}`)
      .pipe(map((meeting) => MeetingsMapper.toMeetingDetail(meeting)));
  }

  public fetchMeetings(query?: MeetingsQuery): Observable<Meeting[]> {
    const params = buildQueryParams(query);
    return this.http
      .get<PaginatedItems<MeetingDto>>(`${this.buildURL({ idOrSubEndpoint: 'Search' })}`, { params })
      .pipe(
        tap((res) => {
          this.setState(res);
        }),
        map((res) => res.items.map((meeting) => MeetingsMapper.toMeeting(meeting)))
      );
  }

  public fetchMeetingTypes(): Observable<MeetingTypeDto[]> {
    this.endPoint = 'Global/TypesSeances';

    return this.http.get<MeetingTypeDto[]>(this.buildURL()).pipe(finalize(() => (this.endPoint = 'Seances')));
  }

  public fetchMeetingRecipients(meetingId: number): Observable<MeetingRecipientDto[]> {
    return this.http.get<MeetingRecipientDto[]>(this.buildURL({ idOrSubEndpoint: `${meetingId}/Destinataires/Full` }));
  }

  public fetchMeetingActionsAndRecipients(
    meetingId: number
  ): Observable<{ actions: MeetingAction[]; recipients: MeetingRecipient[] }> {
    return this.fetchMeetingRecipients(meetingId).pipe(
      map((res: MeetingRecipientDto[]) => {
        const actions = res
          .filter((recipient) => !!recipient.action)
          .map((recipient) => MeetingsMapper.toMeetingAction(recipient, meetingId));
        const recipients = res
          .filter((recipient) => !recipient.action)
          .map((recipient) => PointsMapper.toPointRecipient(recipient));

        return { actions, recipients };
      })
    );
  }

  public fetchNbrMeetingPoints(
    meetingId: number,
    includingPoints: 'includedPoints' | 'excludedPoints' | 'both' = 'both'
  ): Observable<{ pointsInclus: number; pointsNonInclus: number }> {
    let tags = 'PointsInclus,PointsNonInclus';

    if (includingPoints === 'includedPoints') {
      tags = 'PointsInclus';
    }

    if (includingPoints === 'excludedPoints') {
      tags = 'PointsNonInclus';
    }

    return this.http.get<{ pointsInclus: number; pointsNonInclus: number }>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/Count`,
      {
        params: { tags },
      }
    );
  }

  public fetchMeetingParticipants(
    meetingId: number,
    includeOnlyParticipantsWithASignature: boolean = false
  ): Observable<MeetingParticipantDto[]> {
    return this.http.get<MeetingParticipantDto[]>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Participants`, {
      params: { signatureOnly: includeOnlyParticipantsWithASignature },
    });
  }

  public fetchMeetingHistory(meetingId: number): Observable<MeetingHistoryItemDto[]> {
    return this.http.get<MeetingHistoryItemDto[]>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/History`);
  }

  public fetchMeetingIncludedPoints(
    meetingId: number,
    countEvents: boolean = false
  ): Observable<{ nombreEvenements: number; points: IncludedPointDto[] }> {
    return this.http.get<{ nombreEvenements: number; points: IncludedPointDto[] }>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/PointsInclus`,
      {
        params: { countEvenements: countEvents },
      }
    );
  }

  public fetchMeetingNonIncludedPoints(meetingId: number): Observable<PointDto[]> {
    return this.http.get<PointDto[]>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/PointsNonInclus`);
  }

  public fetchMeetingIncludedPointsWithEvents(
    meetingId: number,
    countEvents: boolean = false
  ): Observable<{ nombreEvenements: number; points: PointWithEventsDto[] }> {
    return this.http.get<{ nombreEvenements: number; points: PointWithEventsDto[] }>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/PointsInclusWithEvents`,
      {
        params: { countEvenements: countEvents },
      }
    );
  }

  public fetchMeetingIncludedPointsWithEventsAsTree(
    meetingId: number,
    countEvents: boolean = false
  ): Observable<{ nombreEvenements: number; nombreNiveaux: number; niveaux: PointTreeLevelDto[] }> {
    return this.http.get<{ nombreEvenements: number; nombreNiveaux: number; niveaux: PointTreeLevelDto[] }>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/PointsInclusWithEventsTree`,
      {
        params: { countEvenements: countEvents },
      }
    );
  }

  public fetchMeetingOrderedIncludedPointsWithEvents(
    meetingId: number,
    countEvents: boolean = false
  ): Observable<{ nombreEvenements: number; points: PointWithEventsDto[] }> {
    return this.http.get<{ nombreEvenements: number; points: PointWithEventsDto[] }>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/PointsInclusWithEventsOrdered`,
      {
        params: { countEvenements: countEvents },
      }
    );
  }

  public fetchNbrMeetingPointVotes(meetingId: number, pointId: number): Observable<PointVotesDetailDto> {
    return this.http.get<PointVotesDetailDto>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/${pointId}/Votes`
    );
  }

  public updateOneMeeting(meetingId: number, dto: UpdateMeeting): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}`, dto);
  }

  public updateOneMeetingParticipant(
    meetingId: number,
    participantId: number,
    dto: UpdateMeetingParticipant
  ): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/Participants/${participantId}`,
      dto
    );
  }

  public updateOneMeetingEvent(meetingId: number, eventId: number, dto: UpdateMeetingEvent): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Events/${eventId}`, dto);
  }

  public updateMeetingPointVotes(
    meetingId: number,
    pointId: number,
    dto: UpdateMeetingPointVotes
  ): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/${pointId}/Votes`, dto);
  }

  public deleteMeeting(meetingId: number): Observable<unknown> {
    return this.http.delete<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}`);
  }

  public deleteMeetingParticipants(meetingId: number, participantsIds: number[]): Observable<unknown> {
    return this.http.delete<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Participants`, {
      params: buildQueryParams({ participantIds: participantsIds }),
    });
  }
  public deleteMeetingEvents(meetingId: number, eventsIds: number[]): Observable<unknown> {
    return this.http.delete<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Events`, {
      params: buildQueryParams({ eventIds: eventsIds }),
    });
  }

  private setMeetingsAsOpen(meetingsIds: number[]): Observable<unknown> {
    return this.http.post<unknown>(`${this.buildURL({ idOrSubEndpoint: 'ChangeStatutes' })}`, {
      ids: meetingsIds,
      statut: 'Ouvert',
    });
  }

  private setMeetingsAsClosed(meetingsIds: number[]): Observable<unknown> {
    return this.http.post<unknown>(`${this.buildURL({ idOrSubEndpoint: 'ChangeStatutes' })}`, {
      ids: meetingsIds,
      statut: 'Fermee',
    });
  }

  private setMeetingAsPrepared(meetingsIds: number[]): Observable<unknown> {
    return this.http.post<unknown>(`${this.buildURL({ idOrSubEndpoint: 'ChangeStatutes' })}`, {
      ids: meetingsIds,
      statut: 'Preparee',
    });
  }

  public setMeetingsAs(status: 'open' | 'prepared' | 'closed', meetingsIds: number[]): Observable<unknown> {
    switch (status) {
      case 'open':
        return this.setMeetingsAsOpen(meetingsIds);
      case 'prepared':
        return this.setMeetingAsPrepared(meetingsIds);
      case 'closed':
        return this.setMeetingsAsClosed(meetingsIds);
    }
  }

  public reopenAMeeting(meetingId: number): Observable<unknown> {
    return this.http.put(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Reouvrir`, {});
  }

  public reorderMeetingPointsManually(meetingId: number, reorder: MeetingReorder): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/ReNumerotationsPoints`,
      reorder
    );
  }

  public reorderMeetingPoints(meetingId: number): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/ReNumerotationAuto`, {});
  }

  public moveMeetingPoints(settings: {
    meetingId: number;
    targetPointId: number;
    pointsId: number[];
    beforeTarget: boolean;
  }): Observable<unknown> {
    const { meetingId, targetPointId, pointsId, beforeTarget } = settings;
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/Move`, {
      targetPointId,
      pointsId,
      beforeTarget,
    });
  }

  public recomputeMeetingPresences(meetingId: number): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/RecalculePresences`, {});
  }

  public restoreAMeeting(meetingId: number): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Undelete`, {});
  }

  public removeAPointFromMeeting(meetingId: number, pointId: number): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/${pointId}/Remove`, {});
  }

  public markMeetingPointsAsToBeCorrected(meetingId: number, body: MarkPointsToBeCorrected): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/MarkACorriger`, body);
  }

  public reportMeetingPoints(meetingId: number, body: ReportMeetingPoints): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/Reporter`, body);
  }

  public lockMeetingPoints(meetingId: number, pointsIds: number[]): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/Verrouiller`, {
      pointIds: pointsIds,
    });
  }

  public closeMeetingPoints(meetingId: number, pointsIds: number[]): Observable<unknown> {
    return this.http.put<unknown>(`${this.buildURL({ idOrSubEndpoint: meetingId })}/Points/Cloturer`, {
      pointIds: pointsIds,
    });
  }

  public saveMeetingPointsNewOrder(meetingId: number, pointsIds: number[]): Observable<unknown> {
    return this.http.put(
      this.buildURL({ idOrSubEndpoint: `${meetingId}/Points/ReNumerotationManual` }),
      {},
      { params: { pointIds: `${pointsIds.join(',')}` } }
    );
  }

  public addRecipientsToMeeting(meetingId: number, body: AddRecipientsToMeeting): Observable<unknown> {
    ObjectUtils.removeFalsyEntries(body);

    return this.http.post(this.buildURL({ idOrSubEndpoint: `${meetingId}/Users` }), body);
  }

  public deleteMeetingRecipients(meetingId: number, recipientIds: number | number[]): Observable<unknown> {
    return this.http.delete(this.buildURL({ idOrSubEndpoint: `${meetingId}/Users` }), {
      body: {
        destinataires: Array.isArray(recipientIds)
          ? recipientIds.map((recipientId) => ({ id: recipientId }))
          : [recipientIds].map((recipientId) => ({ id: recipientId })),
      },
    });
  }

  public setMeetingRecipientAsDefault(meetingId: number, recipientId: number): Observable<unknown> {
    return this.http.put(
      this.buildURL({ idOrSubEndpoint: `${meetingId}/Destinataires/${recipientId}/SetPrincipal` }),
      {}
    );
  }
}
